// maomi/src/components/CatRegistration.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Upload, DatePicker, message, Row, Col, Slider, Modal, Tooltip, Checkbox, Select } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { Loop as LoopIcon, AutoFixHigh as AutoFixHighIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { get, post, isAuthenticated } from '../utils/api';
import GradientButton from '../components/GradientButton';
import SelectCity from '../components/SelectCity';
import CatIDCard from './CatIDCard'; 
import './css/CatRegistration.css';

const { Option } = Select;

const CatRegistration = ({ onRegisterSuccess, onClose }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [vaccinationStatus, setVaccinationStatus] = useState({
    first: false,
    second: false,
    third: false,
    rabies: false,
  });
  const [photoUrls, setPhotoUrls] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [registeredCat, setRegisteredCat] = useState(null);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login?pre=cat-registration');
    }
  }, [navigate]);

  const fetchBreeds = async () => {
    try {
      const data = await get('/cat/breeds/');
      setBreeds(data);
    } catch (error) {
      message.error('Failed to fetch breeds');
    }
  };

  const handleSubmit = async (values) => {
    if (!values.breed_id) {
      values.breed_id = 1;
    }
    values.birth_date = values.birth_date ? values.birth_date.format('YYYY-MM-DD') : null;

    // Add vaccination dates to the form data
    values.first_vaccination_date = values.first_vaccination_date ? values.first_vaccination_date.format('YYYY-MM-DD') : null;
    values.second_vaccination_date = values.second_vaccination_date ? values.second_vaccination_date.format('YYYY-MM-DD') : null;
    values.third_vaccination_date = values.third_vaccination_date ? values.third_vaccination_date.format('YYYY-MM-DD') : null;
    values.rabies_vaccination_date = values.rabies_vaccination_date ? values.rabies_vaccination_date.format('YYYY-MM-DD') : null;

    const data = { ...values, photos: photoUrls };
    try {
      const response = await post('/cat/register/', data);
      const uniqueId = response.unique_id;
      const registeredCat = { ...data, unique_id: uniqueId };
      message.success('猫咪注册成功! Unique ID: ' + uniqueId);
      form.resetFields();
      setPhotoUrls([]);
      setRegisteredCat(registeredCat);
      onRegisterSuccess();
    } catch (error) {
      message.error(error.response ? error.response.data.status : '发生错误');
      message.error('发生错误');
    }
  };

  const handleBreedDetection = () => {
    setIsModalVisible(true);
  };

  const handleModalOk = () => {
    setIsModalVisible(false);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const handleUploadChange = async (info) => {
    if (info.file.status === 'done') {
      const url = info.file.response.url;
      setPhotoUrls([...photoUrls, url]);
      message.success(`${info.file.name} 上传成功`);
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} 上传失败`);
    }
  };

  const handleDateChange = (field, date) => {
    if (date) {
      setVaccinationStatus(prevState => ({ ...prevState, [field]: true }));
    }
  };

  const generateCatName = async () => {
    try {
      const response = await post('/cat/generate_cat_name/', {});
      form.setFieldsValue({ name: response.name });
      // message.success('生成名字成功!');
    } catch (error) {
      message.error('生成名字失败');
    }
  };

  return (
    <>
      {!registeredCat ? (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="cat-registration-form"
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="photos"
                label="照片"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }
                  return e && e.fileList;
                }}
              >
                <Upload
                  listType="picture-card"
                  className="photo-upload"
                  onChange={handleUploadChange}
                  multiple={true}
                  action={`${API_BASE_URL}/cat/upload/`}
                  maxCount={10}
                  headers={{
                    'Authorization': `Token ${localStorage.getItem('token')}` // For file upload we need to use the token directly
                  }}
                >
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>点击上传</div>
                  </div>
                </Upload>
              </Form.Item>
              <Form.Item
                name="breed_id"
                label={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span>品种</span>
                    <Tooltip title="AI品种检测">
                      <AutoFixHighIcon
                        style={{ cursor: 'pointer' }}
                        onClick={handleBreedDetection}
                      />
                    </Tooltip>
                  </div>
                }
              >
                <Select placeholder="请选择品种" onClick={fetchBreeds}>
                  {breeds.map(breed => (
                    <Option key={breed.id} value={breed.id}>
                      {breed.name_cn} ({breed.name_en})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={16}>
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item
                    name="name"
                    label="名字"
                    rules={[{ required: true, message: '请输入名字!' }]}
                  >
                    <Input
                      placeholder="名字"
                      addonAfter={
                        <Tooltip title="一键生成名字">
                          <LoopIcon
                            style={{ cursor: 'pointer' }}
                            onClick={generateCatName}
                          />
                        </Tooltip>
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="sex"
                    label="性别"
                    rules={[{ required: true, message: '请选择性别!' }]}
                  >
                    <Select placeholder="请选择性别">
                      <Option value="M">公猫</Option>
                      <Option value="F">母猫</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="birth_city"
                    label="出生市区"
                    rules={[{ required: true, message: '请输入出生城市!' }]}
                  >
                    <SelectCity />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="birth_date"
                    label="出生日期"
                    rules={[{ required: true, message: '请输入出生日期!' }]}
                  >
                    <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={6}>
                <Checkbox checked={vaccinationStatus.first}>猫三联①</Checkbox>
                  <Form.Item name="first_vaccination_date">
                    <DatePicker placeholder="选择日期" onChange={(date) => handleDateChange('first', date)} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                <Checkbox checked={vaccinationStatus.second}>猫三联②</Checkbox>
                  <Form.Item name="second_vaccination_date">
                    <DatePicker placeholder="选择日期" onChange={(date) => handleDateChange('second', date)} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                <Checkbox checked={vaccinationStatus.third}>猫三联③</Checkbox>
                  <Form.Item name="third_vaccination_date">
                    <DatePicker placeholder="选择日期" onChange={(date) => handleDateChange('third', date)} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                <Checkbox checked={vaccinationStatus.rabies}>狂犬病</Checkbox>
                  <Form.Item name="rabies_vaccination_date">
                    <DatePicker placeholder="选择日期" onChange={(date) => handleDateChange('rabies', date)} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="cuteness"
                    label="可爱值"
                  >
                    <Slider min={0} max={10} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="health"
                    label="健康值"
                  >
                    <Slider min={0} max={10} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="clinginess"
                    label="粘人值"
                  >
                    <Slider min={0} max={10} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="gluttony"
                    label="吃货值"
                  >
                    <Slider min={0} max={10} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="sleepiness"
                    label="贪睡值"
                  >
                    <Slider min={0} max={10} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="pettiness"
                    label="撒泼值"
                  >
                    <Slider min={0} max={10} />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
          <Form.Item>
            <GradientButton type="primary" htmlType="submit" className="reg-button">
              注册猫咪
            </GradientButton>
          </Form.Item>

          <Modal
            open={isModalVisible}
            title="AI品种检测（建议上传3张以上照片增加准确率）"
            onOk={handleModalOk}
            onCancel={handleModalCancel}
            footer={[
              <GradientButton key="ok" onClick={handleModalOk}>
                开始检测
              </GradientButton>,
            ]}
          >
            <p></p>
          </Modal>
        </Form>
      ) : (
        <div className="cat-id-card-container">
          <CatIDCard cat={registeredCat} />
          <GradientButton className="ok-button" type="primary" onClick={onClose}>
            好的
          </GradientButton>
        </div>
      )}
    </>
  );
};

export default CatRegistration;
